import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { cn } from '../../utils/cn';
import {
  TrendingUp,
  TrendingDown,
  Minus,
  Plus,
  Maximize2,
  Minimize2,
  Camera,
  Settings,
  BarChart2,
  Activity,
  Layers,
  Grid3x3,
  Eye,
  EyeOff,
  ChevronDown,
  X,
  RefreshCw,
} from 'lucide-react';

interface Kline {
  timestamp: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

interface ChartProps {
  pair: string;
  klines: Kline[];
  isLoading?: boolean;
  height?: number;
  onIntervalChange?: (interval: string) => void;
  currentInterval?: string;
}

interface Indicator {
  id: string;
  name: string;
  shortName: string;
  enabled: boolean;
  color: string;
  params: Record<string, number>;
  pane: 'main' | 'separate';
}

interface DrawingTool {
  id: string;
  name: string;
  icon: React.ComponentType<{ className?: string }>;
  active: boolean;
}

const INTERVALS = [
  { label: '1m', value: '1m' },
  { label: '5m', value: '5m' },
  { label: '15m', value: '15m' },
  { label: '30m', value: '30m' },
  { label: '1H', value: '1h' },
  { label: '4H', value: '4h' },
  { label: '1D', value: '1d' },
  { label: '1W', value: '1w' },
  { label: '1M', value: '1M' },
];

const CHART_TYPES = [
  { id: 'candles', name: 'Candlestick', icon: '🕯️' },
  { id: 'hollowCandles', name: 'Hollow Candles', icon: '📊' },
  { id: 'heikinAshi', name: 'Heikin Ashi', icon: '📈' },
  { id: 'line', name: 'Line', icon: '📉' },
  { id: 'area', name: 'Area', icon: '🗻' },
  { id: 'bars', name: 'Bars', icon: '📶' },
] as const;

type ChartType = typeof CHART_TYPES[number]['id'];

const DEFAULT_INDICATORS: Indicator[] = [
  { id: 'ma7', name: 'Moving Average (7)', shortName: 'MA7', enabled: true, color: '#F0B90B', params: { period: 7 }, pane: 'main' },
  { id: 'ma25', name: 'Moving Average (25)', shortName: 'MA25', enabled: true, color: '#8B5CF6', params: { period: 25 }, pane: 'main' },
  { id: 'ma99', name: 'Moving Average (99)', shortName: 'MA99', enabled: false, color: '#00D4AA', params: { period: 99 }, pane: 'main' },
  { id: 'ema12', name: 'EMA (12)', shortName: 'EMA12', enabled: false, color: '#F97316', params: { period: 12 }, pane: 'main' },
  { id: 'ema26', name: 'EMA (26)', shortName: 'EMA26', enabled: false, color: '#EC4899', params: { period: 26 }, pane: 'main' },
  { id: 'bb', name: 'Bollinger Bands', shortName: 'BB', enabled: false, color: '#6366F1', params: { period: 20, stdDev: 2 }, pane: 'main' },
  { id: 'rsi', name: 'RSI (14)', shortName: 'RSI', enabled: false, color: '#F59E0B', params: { period: 14 }, pane: 'separate' },
  { id: 'macd', name: 'MACD', shortName: 'MACD', enabled: false, color: '#10B981', params: { fast: 12, slow: 26, signal: 9 }, pane: 'separate' },
  { id: 'stoch', name: 'Stochastic', shortName: 'STOCH', enabled: false, color: '#3B82F6', params: { k: 14, d: 3 }, pane: 'separate' },
  { id: 'atr', name: 'ATR (14)', shortName: 'ATR', enabled: false, color: '#EF4444', params: { period: 14 }, pane: 'separate' },
  { id: 'obv', name: 'On Balance Volume', shortName: 'OBV', enabled: false, color: '#06B6D4', params: {}, pane: 'separate' },
  { id: 'vwap', name: 'VWAP', shortName: 'VWAP', enabled: false, color: '#FF6B6B', params: {}, pane: 'main' },
];

// Color schemes
const COLORS = {
  bg: '#080b0f',
  bgSecondary: '#0d1117',
  grid: 'rgba(255, 255, 255, 0.03)',
  gridStrong: 'rgba(255, 255, 255, 0.06)',
  text: '#8b949e',
  textStrong: '#f0f6fc',
  bullish: '#00D4AA',
  bullishTransparent: 'rgba(0, 212, 170, 0.15)',
  bearish: '#FF6B6B',
  bearishTransparent: 'rgba(255, 107, 107, 0.15)',
  crosshair: 'rgba(255, 255, 255, 0.4)',
  priceLine: '#00D4AA',
};

export default function TradingChart({
  pair,
  klines,
  isLoading,
  height = 600,
  onIntervalChange,
  currentInterval = '1h',
}: ChartProps) {
  const mainCanvasRef = useRef<HTMLCanvasElement>(null);
  const overlayCanvasRef = useRef<HTMLCanvasElement>(null);
  const volumeCanvasRef = useRef<HTMLCanvasElement>(null);
  const indicatorCanvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  
  const [chartType, setChartType] = useState<ChartType>('candles');
  const [indicators, setIndicators] = useState<Indicator[]>(DEFAULT_INDICATORS);
  const [showVolume, setShowVolume] = useState(true);
  const [showGrid, setShowGrid] = useState(true);
  const [crosshair, setCrosshair] = useState<{ x: number; y: number; visible: boolean }>({ x: 0, y: 0, visible: false });
  const [tooltip, setTooltip] = useState<Kline | null>(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, pan: 0 });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showIndicatorPanel, setShowIndicatorPanel] = useState(false);
  const [showChartTypeMenu, setShowChartTypeMenu] = useState(false);
  const [autoScale, setAutoScale] = useState(true);
  const [logScale, setLogScale] = useState(false);
  
  // Calculate active separate pane indicators
  const separatePaneIndicators = useMemo(() => 
    indicators.filter(i => i.enabled && i.pane === 'separate'),
    [indicators]
  );

  const hasRSI = separatePaneIndicators.some(i => i.id === 'rsi');
  const hasMACD = separatePaneIndicators.some(i => i.id === 'macd');
  const hasStoch = separatePaneIndicators.some(i => i.id === 'stoch');

  // Get visible klines based on zoom and pan
  const getVisibleKlines = useCallback(() => {
    const baseVisibleCount = 100;
    const visibleCount = Math.floor(baseVisibleCount / zoom);
    const startIndex = Math.max(0, klines.length - visibleCount - pan);
    const endIndex = Math.min(klines.length, startIndex + visibleCount);
    return klines.slice(startIndex, endIndex);
  }, [klines, zoom, pan]);

  // Calculate Heikin Ashi candles
  const calculateHeikinAshi = useCallback((data: Kline[]): Kline[] => {
    const result: Kline[] = [];
    for (let i = 0; i < data.length; i++) {
      const curr = data[i];
      const prev = result[i - 1];
      const haClose = (curr.open + curr.high + curr.low + curr.close) / 4;
      const haOpen = prev ? (prev.open + prev.close) / 2 : (curr.open + curr.close) / 2;
      const haHigh = Math.max(curr.high, haOpen, haClose);
      const haLow = Math.min(curr.low, haOpen, haClose);
      result.push({ ...curr, open: haOpen, high: haHigh, low: haLow, close: haClose });
    }
    return result;
  }, []);

  // Calculate price range with padding
  const calculatePriceRange = useCallback((data: Kline[]) => {
    if (data.length === 0) return { min: 0, max: 100 };
    let min = Infinity;
    let max = -Infinity;
    for (const k of data) {
      if (k.low < min) min = k.low;
      if (k.high > max) max = k.high;
    }
    const padding = (max - min) * 0.08;
    return { min: min - padding, max: max + padding };
  }, []);

  // Calculate indicators
  const calculateMA = useCallback((data: Kline[], period: number): number[] => {
    const result: number[] = [];
    for (let i = 0; i < data.length; i++) {
      if (i < period - 1) {
        result.push(NaN);
      } else {
        let sum = 0;
        for (let j = 0; j < period; j++) sum += data[i - j].close;
        result.push(sum / period);
      }
    }
    return result;
  }, []);

  const calculateEMA = useCallback((data: Kline[], period: number): number[] => {
    const result: number[] = [];
    const multiplier = 2 / (period + 1);
    for (let i = 0; i < data.length; i++) {
      if (i === 0) {
        result.push(data[i].close);
      } else {
        result.push((data[i].close - result[i - 1]) * multiplier + result[i - 1]);
      }
    }
    return result;
  }, []);

  const calculateBollingerBands = useCallback((data: Kline[], period: number, stdDev: number) => {
    const ma = calculateMA(data, period);
    const upper: number[] = [];
    const lower: number[] = [];
    
    for (let i = 0; i < data.length; i++) {
      if (i < period - 1) {
        upper.push(NaN);
        lower.push(NaN);
      } else {
        let sumSquares = 0;
        for (let j = 0; j < period; j++) {
          sumSquares += Math.pow(data[i - j].close - ma[i], 2);
        }
        const std = Math.sqrt(sumSquares / period);
        upper.push(ma[i] + stdDev * std);
        lower.push(ma[i] - stdDev * std);
      }
    }
    return { upper, middle: ma, lower };
  }, [calculateMA]);

  const calculateRSI = useCallback((data: Kline[], period: number): number[] => {
    const result: number[] = [];
    let gains = 0, losses = 0;
    
    for (let i = 0; i < data.length; i++) {
      if (i === 0) {
        result.push(50);
        continue;
      }
      
      const change = data[i].close - data[i - 1].close;
      const gain = change > 0 ? change : 0;
      const loss = change < 0 ? -change : 0;
      
      if (i < period) {
        gains += gain;
        losses += loss;
        result.push(50);
      } else if (i === period) {
        gains += gain;
        losses += loss;
        const avgGain = gains / period;
        const avgLoss = losses / period;
        const rs = avgLoss === 0 ? 100 : avgGain / avgLoss;
        result.push(100 - (100 / (1 + rs)));
      } else {
        const avgGain = (result[i - 1] !== 50 ? ((result[i - 1] / 100) * (period - 1) + gain) / period : gain);
        const avgLoss = (result[i - 1] !== 50 ? ((1 - result[i - 1] / 100) * (period - 1) + loss) / period : loss);
        const rs = avgLoss === 0 ? 100 : avgGain / avgLoss;
        result.push(100 - (100 / (1 + rs)));
      }
    }
    return result;
  }, []);

  const calculateMACD = useCallback((data: Kline[], fast: number, slow: number, signal: number) => {
    const emaFast = calculateEMA(data, fast);
    const emaSlow = calculateEMA(data, slow);
    const macdLine: number[] = [];
    const signalLine: number[] = [];
    const histogram: number[] = [];
    
    for (let i = 0; i < data.length; i++) {
      const macd = emaFast[i] - emaSlow[i];
      macdLine.push(macd);
    }
    
    // Calculate signal line (EMA of MACD)
    const multiplier = 2 / (signal + 1);
    for (let i = 0; i < macdLine.length; i++) {
      if (i === 0) {
        signalLine.push(macdLine[i]);
      } else {
        signalLine.push((macdLine[i] - signalLine[i - 1]) * multiplier + signalLine[i - 1]);
      }
      histogram.push(macdLine[i] - signalLine[i]);
    }
    
    return { macdLine, signalLine, histogram };
  }, [calculateEMA]);

  const calculateVWAP = useCallback((data: Kline[]): number[] => {
    const result: number[] = [];
    let cumulativeTPV = 0;
    let cumulativeVolume = 0;
    
    for (let i = 0; i < data.length; i++) {
      const typicalPrice = (data[i].high + data[i].low + data[i].close) / 3;
      cumulativeTPV += typicalPrice * data[i].volume;
      cumulativeVolume += data[i].volume;
      result.push(cumulativeVolume > 0 ? cumulativeTPV / cumulativeVolume : typicalPrice);
    }
    return result;
  }, []);

  // Format price based on magnitude
  const formatPrice = useCallback((price: number) => {
    if (price >= 10000) return price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    if (price >= 100) return price.toFixed(2);
    if (price >= 1) return price.toFixed(4);
    if (price >= 0.001) return price.toFixed(6);
    return price.toFixed(8);
  }, []);

  // Format time based on interval
  const formatTime = useCallback((timestamp: number) => {
    const date = new Date(timestamp);
    if (currentInterval.includes('m')) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    if (currentInterval === '1h' || currentInterval === '4h') {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ' ' + 
             date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: '2-digit' });
  }, [currentInterval]);

  // Main render effect
  useEffect(() => {
    const mainCanvas = mainCanvasRef.current;
    const overlayCanvas = overlayCanvasRef.current;
    const volumeCanvas = volumeCanvasRef.current;
    const indicatorCanvas = indicatorCanvasRef.current;
    const container = containerRef.current;
    
    if (!mainCanvas || !overlayCanvas || !container) return;

    const mainCtx = mainCanvas.getContext('2d');
    const overlayCtx = overlayCanvas.getContext('2d');
    const volumeCtx = volumeCanvas?.getContext('2d');
    const indicatorCtx = indicatorCanvas?.getContext('2d');
    
    if (!mainCtx || !overlayCtx) return;

    const rect = container.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    
    // Calculate heights
    const volumeHeight = showVolume ? 80 : 0;
    const indicatorHeight = separatePaneIndicators.length * 100;
    const mainChartHeight = rect.height - volumeHeight - indicatorHeight - 50;
    
    // Setup canvases
    mainCanvas.width = rect.width * dpr;
    mainCanvas.height = mainChartHeight * dpr;
    mainCanvas.style.height = `${mainChartHeight}px`;
    mainCtx.scale(dpr, dpr);
    
    overlayCanvas.width = rect.width * dpr;
    overlayCanvas.height = rect.height * dpr;
    overlayCtx.scale(dpr, dpr);
    
    if (volumeCanvas && volumeCtx) {
      volumeCanvas.width = rect.width * dpr;
      volumeCanvas.height = volumeHeight * dpr;
      volumeCanvas.style.height = `${volumeHeight}px`;
      volumeCtx.scale(dpr, dpr);
    }
    
    if (indicatorCanvas && indicatorCtx) {
      indicatorCanvas.width = rect.width * dpr;
      indicatorCanvas.height = indicatorHeight * dpr;
      indicatorCanvas.style.height = `${indicatorHeight}px`;
      indicatorCtx.scale(dpr, dpr);
    }

    const width = rect.width;
    const priceAxisWidth = 70;
    const chartWidth = width - priceAxisWidth;

    // Clear canvases
    mainCtx.fillStyle = COLORS.bg;
    mainCtx.fillRect(0, 0, width, mainChartHeight);
    
    overlayCtx.clearRect(0, 0, width, rect.height);
    
    if (volumeCtx) {
      volumeCtx.fillStyle = COLORS.bg;
      volumeCtx.fillRect(0, 0, width, volumeHeight);
    }
    
    if (indicatorCtx) {
      indicatorCtx.fillStyle = COLORS.bg;
      indicatorCtx.fillRect(0, 0, width, indicatorHeight);
    }

    // Get visible data
    let visibleKlines = getVisibleKlines();
    if (visibleKlines.length === 0) return;

    // Apply Heikin Ashi if selected
    if (chartType === 'heikinAshi') {
      visibleKlines = calculateHeikinAshi(visibleKlines);
    }

    const { min: priceMin, max: priceMax } = calculatePriceRange(visibleKlines);
    const priceRange = priceMax - priceMin;

    // Calculate candle dimensions
    const candleWidth = Math.max(3, (chartWidth - 20) / visibleKlines.length - 1);
    const candleGap = Math.max(1, candleWidth * 0.15);
    const actualCandleWidth = candleWidth - candleGap;

    // Coordinate helpers
    const priceToY = (price: number) => {
      if (logScale && price > 0 && priceMin > 0) {
        const logMin = Math.log(priceMin);
        const logMax = Math.log(priceMax);
        const logPrice = Math.log(price);
        return 30 + (1 - (logPrice - logMin) / (logMax - logMin)) * (mainChartHeight - 60);
      }
      return 30 + (1 - (price - priceMin) / priceRange) * (mainChartHeight - 60);
    };
    
    const indexToX = (index: number) => 10 + index * candleWidth + candleWidth / 2;

    // Draw grid
    if (showGrid) {
      mainCtx.strokeStyle = COLORS.grid;
      mainCtx.lineWidth = 1;
      
      // Horizontal grid lines
      const priceSteps = 8;
      for (let i = 0; i <= priceSteps; i++) {
        const y = 30 + (i / priceSteps) * (mainChartHeight - 60);
        mainCtx.beginPath();
        mainCtx.moveTo(0, y);
        mainCtx.lineTo(chartWidth, y);
        mainCtx.stroke();
      }
      
      // Vertical grid lines
      const timeSteps = Math.min(10, Math.floor(visibleKlines.length / 10));
      for (let i = 0; i <= timeSteps; i++) {
        const x = 10 + (i / timeSteps) * (chartWidth - 20);
        mainCtx.beginPath();
        mainCtx.moveTo(x, 0);
        mainCtx.lineTo(x, mainChartHeight);
        mainCtx.stroke();
      }
    }

    // Draw price axis
    mainCtx.fillStyle = COLORS.bgSecondary;
    mainCtx.fillRect(chartWidth, 0, priceAxisWidth, mainChartHeight);
    
    mainCtx.strokeStyle = COLORS.gridStrong;
    mainCtx.lineWidth = 1;
    mainCtx.beginPath();
    mainCtx.moveTo(chartWidth, 0);
    mainCtx.lineTo(chartWidth, mainChartHeight);
    mainCtx.stroke();
    
    mainCtx.fillStyle = COLORS.text;
    mainCtx.font = '11px "JetBrains Mono", monospace';
    mainCtx.textAlign = 'left';
    
    for (let i = 0; i <= 8; i++) {
      const price = priceMax - (i / 8) * priceRange;
      const y = priceToY(price);
      mainCtx.fillText(formatPrice(price), chartWidth + 8, y + 4);
    }

    // Draw candles
    for (let i = 0; i < visibleKlines.length; i++) {
      const k = visibleKlines[i];
      const x = indexToX(i);
      const isGreen = k.close >= k.open;
      
      const bodyTop = priceToY(Math.max(k.open, k.close));
      const bodyBottom = priceToY(Math.min(k.open, k.close));
      const wickTop = priceToY(k.high);
      const wickBottom = priceToY(k.low);
      const bodyHeight = Math.max(1, bodyBottom - bodyTop);

      // Draw wick
      mainCtx.strokeStyle = isGreen ? COLORS.bullish : COLORS.bearish;
      mainCtx.lineWidth = 1;
      mainCtx.beginPath();
      mainCtx.moveTo(x, wickTop);
      mainCtx.lineTo(x, wickBottom);
      mainCtx.stroke();

      // Draw body based on chart type
      if (chartType === 'candles' || chartType === 'heikinAshi') {
        mainCtx.fillStyle = isGreen ? COLORS.bullish : COLORS.bearish;
        mainCtx.fillRect(x - actualCandleWidth / 2, bodyTop, actualCandleWidth, bodyHeight);
      } else if (chartType === 'hollowCandles') {
        if (isGreen) {
          mainCtx.strokeStyle = COLORS.bullish;
          mainCtx.strokeRect(x - actualCandleWidth / 2, bodyTop, actualCandleWidth, bodyHeight);
        } else {
          mainCtx.fillStyle = COLORS.bearish;
          mainCtx.fillRect(x - actualCandleWidth / 2, bodyTop, actualCandleWidth, bodyHeight);
        }
      } else if (chartType === 'bars') {
        mainCtx.beginPath();
        mainCtx.moveTo(x - actualCandleWidth / 3, priceToY(k.open));
        mainCtx.lineTo(x, priceToY(k.open));
        mainCtx.moveTo(x, wickTop);
        mainCtx.lineTo(x, wickBottom);
        mainCtx.moveTo(x, priceToY(k.close));
        mainCtx.lineTo(x + actualCandleWidth / 3, priceToY(k.close));
        mainCtx.stroke();
      }
    }

    // Draw line/area chart
    if (chartType === 'line' || chartType === 'area') {
      mainCtx.beginPath();
      mainCtx.strokeStyle = COLORS.bullish;
      mainCtx.lineWidth = 2;
      
      for (let i = 0; i < visibleKlines.length; i++) {
        const x = indexToX(i);
        const y = priceToY(visibleKlines[i].close);
        if (i === 0) mainCtx.moveTo(x, y);
        else mainCtx.lineTo(x, y);
      }
      mainCtx.stroke();

      if (chartType === 'area') {
        mainCtx.lineTo(indexToX(visibleKlines.length - 1), mainChartHeight - 30);
        mainCtx.lineTo(indexToX(0), mainChartHeight - 30);
        mainCtx.closePath();
        const gradient = mainCtx.createLinearGradient(0, 0, 0, mainChartHeight);
        gradient.addColorStop(0, COLORS.bullishTransparent);
        gradient.addColorStop(1, 'transparent');
        mainCtx.fillStyle = gradient;
        mainCtx.fill();
      }
    }

    // Draw indicators
    const enabledIndicators = indicators.filter(i => i.enabled && i.pane === 'main');
    
    for (const indicator of enabledIndicators) {
      mainCtx.strokeStyle = indicator.color;
      mainCtx.lineWidth = 1.5;
      
      if (indicator.id.startsWith('ma')) {
        const ma = calculateMA(visibleKlines, indicator.params.period);
        mainCtx.beginPath();
        let started = false;
        for (let i = 0; i < ma.length; i++) {
          if (isNaN(ma[i])) continue;
          const x = indexToX(i);
          const y = priceToY(ma[i]);
          if (!started) { mainCtx.moveTo(x, y); started = true; }
          else mainCtx.lineTo(x, y);
        }
        mainCtx.stroke();
      }
      
      if (indicator.id.startsWith('ema')) {
        const ema = calculateEMA(visibleKlines, indicator.params.period);
        mainCtx.beginPath();
        for (let i = 0; i < ema.length; i++) {
          const x = indexToX(i);
          const y = priceToY(ema[i]);
          if (i === 0) mainCtx.moveTo(x, y);
          else mainCtx.lineTo(x, y);
        }
        mainCtx.stroke();
      }
      
      if (indicator.id === 'bb') {
        const bb = calculateBollingerBands(visibleKlines, indicator.params.period, indicator.params.stdDev);
        
        // Upper band
        mainCtx.strokeStyle = indicator.color;
        mainCtx.setLineDash([5, 5]);
        mainCtx.beginPath();
        let started = false;
        for (let i = 0; i < bb.upper.length; i++) {
          if (isNaN(bb.upper[i])) continue;
          const x = indexToX(i);
          if (!started) { mainCtx.moveTo(x, priceToY(bb.upper[i])); started = true; }
          else mainCtx.lineTo(x, priceToY(bb.upper[i]));
        }
        mainCtx.stroke();
        
        // Lower band
        mainCtx.beginPath();
        started = false;
        for (let i = 0; i < bb.lower.length; i++) {
          if (isNaN(bb.lower[i])) continue;
          const x = indexToX(i);
          if (!started) { mainCtx.moveTo(x, priceToY(bb.lower[i])); started = true; }
          else mainCtx.lineTo(x, priceToY(bb.lower[i]));
        }
        mainCtx.stroke();
        mainCtx.setLineDash([]);
        
        // Middle band (MA)
        mainCtx.beginPath();
        started = false;
        for (let i = 0; i < bb.middle.length; i++) {
          if (isNaN(bb.middle[i])) continue;
          const x = indexToX(i);
          if (!started) { mainCtx.moveTo(x, priceToY(bb.middle[i])); started = true; }
          else mainCtx.lineTo(x, priceToY(bb.middle[i]));
        }
        mainCtx.stroke();
      }
      
      if (indicator.id === 'vwap') {
        const vwap = calculateVWAP(visibleKlines);
        mainCtx.beginPath();
        for (let i = 0; i < vwap.length; i++) {
          const x = indexToX(i);
          const y = priceToY(vwap[i]);
          if (i === 0) mainCtx.moveTo(x, y);
          else mainCtx.lineTo(x, y);
        }
        mainCtx.stroke();
      }
    }

    // Draw volume
    if (showVolume && volumeCtx) {
      const maxVolume = Math.max(...visibleKlines.map(k => k.volume));
      
      for (let i = 0; i < visibleKlines.length; i++) {
        const k = visibleKlines[i];
        const x = indexToX(i);
        const isGreen = k.close >= k.open;
        const barHeight = (k.volume / maxVolume) * (volumeHeight - 15);
        
        volumeCtx.fillStyle = isGreen ? COLORS.bullishTransparent : COLORS.bearishTransparent;
        volumeCtx.fillRect(x - actualCandleWidth / 2, volumeHeight - barHeight - 5, actualCandleWidth, barHeight);
        
        volumeCtx.strokeStyle = isGreen ? COLORS.bullish : COLORS.bearish;
        volumeCtx.lineWidth = 1;
        volumeCtx.strokeRect(x - actualCandleWidth / 2, volumeHeight - barHeight - 5, actualCandleWidth, barHeight);
      }
      
      // Volume axis
      volumeCtx.fillStyle = COLORS.bgSecondary;
      volumeCtx.fillRect(chartWidth, 0, priceAxisWidth, volumeHeight);
      volumeCtx.fillStyle = COLORS.text;
      volumeCtx.font = '10px "JetBrains Mono", monospace';
      volumeCtx.textAlign = 'left';
      volumeCtx.fillText('Vol', chartWidth + 8, 15);
      
      const formatVol = (v: number) => {
        if (v >= 1e9) return (v / 1e9).toFixed(1) + 'B';
        if (v >= 1e6) return (v / 1e6).toFixed(1) + 'M';
        if (v >= 1e3) return (v / 1e3).toFixed(1) + 'K';
        return v.toFixed(0);
      };
      volumeCtx.fillText(formatVol(maxVolume), chartWidth + 8, 35);
    }

    // Draw RSI indicator
    if (hasRSI && indicatorCtx) {
      const rsiIndicator = indicators.find(i => i.id === 'rsi')!;
      const rsi = calculateRSI(visibleKlines, rsiIndicator.params.period);
      const paneHeight = 100;
      const paneY = 0;
      
      // Background
      indicatorCtx.fillStyle = COLORS.bg;
      indicatorCtx.fillRect(0, paneY, width, paneHeight);
      
      // Border
      indicatorCtx.strokeStyle = COLORS.gridStrong;
      indicatorCtx.lineWidth = 1;
      indicatorCtx.beginPath();
      indicatorCtx.moveTo(0, paneY);
      indicatorCtx.lineTo(width, paneY);
      indicatorCtx.stroke();
      
      // Overbought/oversold lines
      indicatorCtx.strokeStyle = COLORS.grid;
      indicatorCtx.setLineDash([3, 3]);
      [30, 50, 70].forEach(level => {
        const y = paneY + 10 + (1 - level / 100) * (paneHeight - 20);
        indicatorCtx.beginPath();
        indicatorCtx.moveTo(0, y);
        indicatorCtx.lineTo(chartWidth, y);
        indicatorCtx.stroke();
      });
      indicatorCtx.setLineDash([]);
      
      // RSI line
      indicatorCtx.strokeStyle = rsiIndicator.color;
      indicatorCtx.lineWidth = 1.5;
      indicatorCtx.beginPath();
      for (let i = 0; i < rsi.length; i++) {
        const x = indexToX(i);
        const y = paneY + 10 + (1 - rsi[i] / 100) * (paneHeight - 20);
        if (i === 0) indicatorCtx.moveTo(x, y);
        else indicatorCtx.lineTo(x, y);
      }
      indicatorCtx.stroke();
      
      // Labels
      indicatorCtx.fillStyle = COLORS.bgSecondary;
      indicatorCtx.fillRect(chartWidth, paneY, priceAxisWidth, paneHeight);
      indicatorCtx.fillStyle = COLORS.text;
      indicatorCtx.font = '10px "JetBrains Mono", monospace';
      indicatorCtx.textAlign = 'left';
      indicatorCtx.fillText('RSI(14)', chartWidth + 8, paneY + 15);
      indicatorCtx.fillText('70', chartWidth + 8, paneY + 30);
      indicatorCtx.fillText('30', chartWidth + 8, paneY + paneHeight - 15);
    }

    // Draw MACD indicator
    if (hasMACD && indicatorCtx) {
      const macdIndicator = indicators.find(i => i.id === 'macd')!;
      const { macdLine, signalLine, histogram } = calculateMACD(
        visibleKlines, 
        macdIndicator.params.fast, 
        macdIndicator.params.slow, 
        macdIndicator.params.signal
      );
      
      const paneHeight = 100;
      const paneY = hasRSI ? 100 : 0;
      
      // Background
      indicatorCtx.fillStyle = COLORS.bg;
      indicatorCtx.fillRect(0, paneY, width, paneHeight);
      
      // Border
      indicatorCtx.strokeStyle = COLORS.gridStrong;
      indicatorCtx.lineWidth = 1;
      indicatorCtx.beginPath();
      indicatorCtx.moveTo(0, paneY);
      indicatorCtx.lineTo(width, paneY);
      indicatorCtx.stroke();
      
      // Zero line
      const zeroY = paneY + paneHeight / 2;
      indicatorCtx.strokeStyle = COLORS.grid;
      indicatorCtx.beginPath();
      indicatorCtx.moveTo(0, zeroY);
      indicatorCtx.lineTo(chartWidth, zeroY);
      indicatorCtx.stroke();
      
      // Find range
      const allValues = [...macdLine, ...signalLine, ...histogram].filter(v => !isNaN(v));
      const macdMax = Math.max(...allValues.map(Math.abs)) * 1.2;
      const macdToY = (v: number) => zeroY - (v / macdMax) * (paneHeight / 2 - 10);
      
      // Histogram
      for (let i = 0; i < histogram.length; i++) {
        const x = indexToX(i);
        const h = histogram[i];
        if (isNaN(h)) continue;
        const y = macdToY(h);
        const barWidth = actualCandleWidth * 0.8;
        indicatorCtx.fillStyle = h >= 0 ? COLORS.bullishTransparent : COLORS.bearishTransparent;
        indicatorCtx.fillRect(x - barWidth / 2, Math.min(y, zeroY), barWidth, Math.abs(y - zeroY));
      }
      
      // MACD line
      indicatorCtx.strokeStyle = macdIndicator.color;
      indicatorCtx.lineWidth = 1.5;
      indicatorCtx.beginPath();
      for (let i = 0; i < macdLine.length; i++) {
        if (isNaN(macdLine[i])) continue;
        const x = indexToX(i);
        const y = macdToY(macdLine[i]);
        if (i === 0) indicatorCtx.moveTo(x, y);
        else indicatorCtx.lineTo(x, y);
      }
      indicatorCtx.stroke();
      
      // Signal line
      indicatorCtx.strokeStyle = '#EF4444';
      indicatorCtx.beginPath();
      for (let i = 0; i < signalLine.length; i++) {
        if (isNaN(signalLine[i])) continue;
        const x = indexToX(i);
        const y = macdToY(signalLine[i]);
        if (i === 0) indicatorCtx.moveTo(x, y);
        else indicatorCtx.lineTo(x, y);
      }
      indicatorCtx.stroke();
      
      // Labels
      indicatorCtx.fillStyle = COLORS.bgSecondary;
      indicatorCtx.fillRect(chartWidth, paneY, priceAxisWidth, paneHeight);
      indicatorCtx.fillStyle = COLORS.text;
      indicatorCtx.font = '10px "JetBrains Mono", monospace';
      indicatorCtx.textAlign = 'left';
      indicatorCtx.fillText('MACD', chartWidth + 8, paneY + 15);
    }

    // Draw current price line
    if (visibleKlines.length > 0) {
      const lastPrice = visibleKlines[visibleKlines.length - 1].close;
      const y = priceToY(lastPrice);
      const prevPrice = visibleKlines.length > 1 ? visibleKlines[visibleKlines.length - 2].close : lastPrice;
      const isUp = lastPrice >= prevPrice;
      
      overlayCtx.strokeStyle = isUp ? COLORS.bullish : COLORS.bearish;
      overlayCtx.lineWidth = 1;
      overlayCtx.setLineDash([4, 4]);
      overlayCtx.beginPath();
      overlayCtx.moveTo(0, y);
      overlayCtx.lineTo(chartWidth, y);
      overlayCtx.stroke();
      overlayCtx.setLineDash([]);

      // Price badge
      overlayCtx.fillStyle = isUp ? COLORS.bullish : COLORS.bearish;
      overlayCtx.beginPath();
      overlayCtx.roundRect(chartWidth, y - 12, priceAxisWidth, 24, 4);
      overlayCtx.fill();
      
      overlayCtx.fillStyle = '#000';
      overlayCtx.font = 'bold 11px "JetBrains Mono", monospace';
      overlayCtx.textAlign = 'left';
      overlayCtx.fillText(formatPrice(lastPrice), chartWidth + 8, y + 4);
    }

    // Draw crosshair
    if (crosshair.visible) {
      overlayCtx.strokeStyle = COLORS.crosshair;
      overlayCtx.lineWidth = 1;
      overlayCtx.setLineDash([3, 3]);
      
      // Horizontal line
      overlayCtx.beginPath();
      overlayCtx.moveTo(0, crosshair.y);
      overlayCtx.lineTo(chartWidth, crosshair.y);
      overlayCtx.stroke();
      
      // Vertical line
      overlayCtx.beginPath();
      overlayCtx.moveTo(crosshair.x, 0);
      overlayCtx.lineTo(crosshair.x, rect.height);
      overlayCtx.stroke();
      overlayCtx.setLineDash([]);

      // Price label
      if (crosshair.y < mainChartHeight) {
        const price = priceMax - ((crosshair.y - 30) / (mainChartHeight - 60)) * priceRange;
        overlayCtx.fillStyle = '#1e2329';
        overlayCtx.beginPath();
        overlayCtx.roundRect(chartWidth, crosshair.y - 10, priceAxisWidth - 5, 20, 3);
        overlayCtx.fill();
        overlayCtx.fillStyle = COLORS.textStrong;
        overlayCtx.font = '11px "JetBrains Mono", monospace';
        overlayCtx.textAlign = 'left';
        overlayCtx.fillText(formatPrice(price), chartWidth + 6, crosshair.y + 4);
      }

      // Time label
      const candleIndex = Math.floor((crosshair.x - 10) / candleWidth);
      if (candleIndex >= 0 && candleIndex < visibleKlines.length) {
        const time = formatTime(visibleKlines[candleIndex].timestamp);
        overlayCtx.fillStyle = '#1e2329';
        const textWidth = overlayCtx.measureText(time).width;
        overlayCtx.beginPath();
        overlayCtx.roundRect(crosshair.x - textWidth / 2 - 8, rect.height - 20, textWidth + 16, 18, 3);
        overlayCtx.fill();
        overlayCtx.fillStyle = COLORS.textStrong;
        overlayCtx.textAlign = 'center';
        overlayCtx.fillText(time, crosshair.x, rect.height - 7);
      }
    }

  }, [klines, chartType, indicators, showGrid, showVolume, crosshair, zoom, pan, getVisibleKlines, calculatePriceRange, calculateHeikinAshi, calculateMA, calculateEMA, calculateBollingerBands, calculateRSI, calculateMACD, calculateVWAP, formatPrice, formatTime, separatePaneIndicators, hasRSI, hasMACD, hasStoch, logScale]);

  // Mouse handlers
  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;

    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    setCrosshair({ x, y, visible: true });

    const visibleKlines = getVisibleKlines();
    const chartWidth = rect.width - 70;
    const candleWidth = Math.max(3, (chartWidth - 20) / visibleKlines.length - 1);
    const index = Math.floor((x - 10) / candleWidth);
    
    if (index >= 0 && index < visibleKlines.length) {
      setTooltip(visibleKlines[index]);
    }

    if (isDragging) {
      const dx = dragStart.x - x;
      const newPan = dragStart.pan + Math.floor(dx / candleWidth);
      setPan(Math.max(0, Math.min(klines.length - 50, newPan)));
    }
  }, [isDragging, dragStart, klines.length, getVisibleKlines]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    setIsDragging(true);
    setDragStart({ x: e.clientX - rect.left, pan });
  }, [pan]);

  const handleMouseUp = useCallback(() => setIsDragging(false), []);
  
  const handleMouseLeave = useCallback(() => {
    setCrosshair(prev => ({ ...prev, visible: false }));
    setTooltip(null);
    setIsDragging(false);
  }, []);

  const handleWheel = useCallback((e: WheelEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const delta = e.deltaY > 0 ? -0.15 : 0.15;
    setZoom(prev => Math.max(0.3, Math.min(8, prev + delta)));
  }, []);

  // Add non-passive wheel event listener to prevent page zoom
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      container.removeEventListener('wheel', handleWheel);
    };
  }, [handleWheel]);

  const toggleIndicator = useCallback((id: string) => {
    setIndicators(prev => prev.map(i => 
      i.id === id ? { ...i, enabled: !i.enabled } : i
    ));
  }, []);

  const handleFullscreen = useCallback(() => {
    if (!isFullscreen) {
      containerRef.current?.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
    setIsFullscreen(!isFullscreen);
  }, [isFullscreen]);

  const handleScreenshot = useCallback(() => {
    const canvas = mainCanvasRef.current;
    if (!canvas) return;
    const link = document.createElement('a');
    link.download = `${pair}-chart-${new Date().toISOString()}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  }, [pair]);

  const volumeHeight = showVolume ? 80 : 0;
  const indicatorHeight = separatePaneIndicators.length * 100;

  return (
    <div 
      className={cn(
        "flex flex-col bg-[#080b0f] select-none relative",
        isFullscreen && "fixed inset-0 z-50"
      )}
      style={{ height: isFullscreen ? '100vh' : height }}
    >
      {/* Enhanced Toolbar */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-white/[0.06] bg-[#0d1117]">
        {/* Left section */}
        <div className="flex items-center gap-2">
          {/* Time intervals */}
          <div className="flex items-center bg-white/[0.03] rounded-lg p-0.5">
            {INTERVALS.map(({ label, value }) => (
              <button
                key={value}
                onClick={() => onIntervalChange?.(value)}
                className={cn(
                  "px-2.5 py-1.5 text-xs font-medium rounded-md transition-all",
                  currentInterval === value
                    ? "bg-[#00d4aa] text-[#080b0f]"
                    : "text-[#8b949e] hover:text-white hover:bg-white/[0.06]"
                )}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="w-px h-6 bg-white/[0.08]" />

          {/* Chart type dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowChartTypeMenu(!showChartTypeMenu)}
              className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-lg bg-white/[0.03] text-[#8b949e] hover:text-white hover:bg-white/[0.06] transition-all"
            >
              {CHART_TYPES.find(t => t.id === chartType)?.icon} {CHART_TYPES.find(t => t.id === chartType)?.name}
              <ChevronDown className="h-3 w-3" />
            </button>
            
            {showChartTypeMenu && (
              <div className="absolute top-full left-0 mt-1 bg-[#161b22] border border-white/[0.1] rounded-lg shadow-xl z-50 overflow-hidden min-w-[160px]">
                {CHART_TYPES.map(type => (
                  <button
                    key={type.id}
                    onClick={() => { setChartType(type.id); setShowChartTypeMenu(false); }}
                    className={cn(
                      "w-full flex items-center gap-2 px-3 py-2 text-xs hover:bg-white/[0.06] transition-colors",
                      chartType === type.id ? "text-[#00d4aa] bg-[#00d4aa]/10" : "text-[#8b949e]"
                    )}
                  >
                    <span>{type.icon}</span>
                    <span>{type.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Center section - Indicators */}
        <div className="flex items-center gap-1">
          {indicators.filter(i => i.enabled).map(indicator => (
            <button
              key={indicator.id}
              onClick={() => toggleIndicator(indicator.id)}
              className="flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded-md transition-all"
              style={{ 
                backgroundColor: `${indicator.color}15`,
                color: indicator.color,
              }}
            >
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: indicator.color }} />
              {indicator.shortName}
              <X className="h-3 w-3 opacity-60 hover:opacity-100" />
            </button>
          ))}
          
          <button
            onClick={() => setShowIndicatorPanel(!showIndicatorPanel)}
            className={cn(
              "flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md transition-all",
              showIndicatorPanel 
                ? "bg-[#00d4aa]/10 text-[#00d4aa]" 
                : "text-[#8b949e] hover:text-white hover:bg-white/[0.06]"
            )}
          >
            <Activity className="h-3.5 w-3.5" />
            Indicators
          </button>
        </div>

        {/* Right section - Controls */}
        <div className="flex items-center gap-1">
          <button
            onClick={() => setShowGrid(!showGrid)}
            className={cn(
              "p-1.5 rounded-lg transition-all",
              showGrid ? "text-[#00d4aa] bg-[#00d4aa]/10" : "text-[#8b949e] hover:text-white hover:bg-white/[0.06]"
            )}
            title="Toggle grid"
          >
            <Grid3x3 className="h-4 w-4" />
          </button>
          
          <button
            onClick={() => setShowVolume(!showVolume)}
            className={cn(
              "p-1.5 rounded-lg transition-all",
              showVolume ? "text-[#00d4aa] bg-[#00d4aa]/10" : "text-[#8b949e] hover:text-white hover:bg-white/[0.06]"
            )}
            title="Toggle volume"
          >
            <BarChart2 className="h-4 w-4" />
          </button>
          
          <button
            onClick={() => setLogScale(!logScale)}
            className={cn(
              "p-1.5 rounded-lg transition-all text-xs font-bold",
              logScale ? "text-[#00d4aa] bg-[#00d4aa]/10" : "text-[#8b949e] hover:text-white hover:bg-white/[0.06]"
            )}
            title="Toggle log scale"
          >
            log
          </button>

          <div className="w-px h-5 bg-white/[0.08] mx-1" />

          <button
            onClick={() => setZoom(prev => Math.min(8, prev + 0.3))}
            className="p-1.5 rounded-lg text-[#8b949e] hover:text-white hover:bg-white/[0.06] transition-all"
            title="Zoom in"
          >
            <Plus className="h-4 w-4" />
          </button>
          
          <button
            onClick={() => setZoom(prev => Math.max(0.3, prev - 0.3))}
            className="p-1.5 rounded-lg text-[#8b949e] hover:text-white hover:bg-white/[0.06] transition-all"
            title="Zoom out"
          >
            <Minus className="h-4 w-4" />
          </button>
          
          <button
            onClick={() => { setZoom(1); setPan(0); }}
            className="p-1.5 rounded-lg text-[#8b949e] hover:text-white hover:bg-white/[0.06] transition-all"
            title="Reset view"
          >
            <RefreshCw className="h-4 w-4" />
          </button>

          <div className="w-px h-5 bg-white/[0.08] mx-1" />

          <button
            onClick={handleScreenshot}
            className="p-1.5 rounded-lg text-[#8b949e] hover:text-white hover:bg-white/[0.06] transition-all"
            title="Screenshot"
          >
            <Camera className="h-4 w-4" />
          </button>
          
          <button
            onClick={handleFullscreen}
            className="p-1.5 rounded-lg text-[#8b949e] hover:text-white hover:bg-white/[0.06] transition-all"
            title="Fullscreen"
          >
            {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {/* Indicator Panel */}
      {showIndicatorPanel && (
        <div className="absolute top-12 left-1/2 -translate-x-1/2 z-50 bg-[#161b22] border border-white/[0.1] rounded-xl shadow-2xl p-4 min-w-[400px]">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-white">Technical Indicators</h3>
            <button 
              onClick={() => setShowIndicatorPanel(false)}
              className="p-1 rounded hover:bg-white/[0.06] text-[#8b949e]"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          
          <div className="grid grid-cols-2 gap-2">
            {indicators.map(indicator => (
              <button
                key={indicator.id}
                onClick={() => toggleIndicator(indicator.id)}
                className={cn(
                  "flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all text-left",
                  indicator.enabled 
                    ? "bg-white/[0.08] text-white" 
                    : "text-[#8b949e] hover:bg-white/[0.04] hover:text-white"
                )}
              >
                <span 
                  className="w-3 h-3 rounded-full flex-shrink-0" 
                  style={{ backgroundColor: indicator.color }}
                />
                <span className="flex-1">{indicator.name}</span>
                {indicator.enabled && (
                  <span className="text-xs text-[#00d4aa]">✓</span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Chart Container - wheel event handled via useEffect with passive: false */}
      <div
        ref={containerRef}
        className="relative flex-1 cursor-crosshair overflow-hidden"
        onMouseMove={handleMouseMove}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
      >
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-[#080b0f]/90 z-10">
            <div className="flex flex-col items-center gap-3">
              <div className="w-8 h-8 border-2 border-[#00d4aa] border-t-transparent rounded-full animate-spin" />
              <span className="text-sm text-[#8b949e]">Loading chart data...</span>
            </div>
          </div>
        )}

        {/* OHLCV Tooltip */}
        {tooltip && crosshair.visible && (
          <div
            className="absolute z-30 pointer-events-none"
            style={{
              left: 12,
              top: 8,
            }}
          >
            <div className="flex items-center gap-4 text-xs">
              <span className="text-[#8b949e]">
                {new Date(tooltip.timestamp).toLocaleString()}
              </span>
              <span className="text-[#8b949e]">O <span className="text-white font-mono">{formatPrice(tooltip.open)}</span></span>
              <span className="text-[#8b949e]">H <span className="text-[#00d4aa] font-mono">{formatPrice(tooltip.high)}</span></span>
              <span className="text-[#8b949e]">L <span className="text-[#ff6b6b] font-mono">{formatPrice(tooltip.low)}</span></span>
              <span className="text-[#8b949e]">C <span className={cn("font-mono", tooltip.close >= tooltip.open ? "text-[#00d4aa]" : "text-[#ff6b6b]")}>{formatPrice(tooltip.close)}</span></span>
              <span className="text-[#8b949e]">Vol <span className="text-white font-mono">{tooltip.volume.toLocaleString()}</span></span>
              {tooltip.close !== tooltip.open && (
                <span className={cn(
                  "font-medium",
                  tooltip.close >= tooltip.open ? "text-[#00d4aa]" : "text-[#ff6b6b]"
                )}>
                  {((tooltip.close - tooltip.open) / tooltip.open * 100).toFixed(2)}%
                </span>
              )}
            </div>
          </div>
        )}

        {/* Main Chart Canvas */}
        <canvas
          ref={mainCanvasRef}
          className="absolute top-0 left-0 w-full"
        />

        {/* Volume Canvas */}
        {showVolume && (
          <canvas
            ref={volumeCanvasRef}
            className="absolute left-0 w-full"
            style={{ top: `calc(100% - ${volumeHeight + indicatorHeight}px)` }}
          />
        )}

        {/* Indicator Panes Canvas */}
        {separatePaneIndicators.length > 0 && (
          <canvas
            ref={indicatorCanvasRef}
            className="absolute left-0 w-full bottom-0"
          />
        )}

        {/* Overlay Canvas (crosshair, price line) */}
        <canvas
          ref={overlayCanvasRef}
          className="absolute inset-0 w-full h-full pointer-events-none"
        />
      </div>
    </div>
  );
}
